import { useState } from "react";
import { ChevronDown, ChevronUp, Copy, CheckCircle } from "lucide-react";

/* ─── Collapsible Section ─── */
const Section = ({ id, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} className="border border-white/10 rounded-xl overflow-hidden mb-4 bg-[#0d1424]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors text-left">
        <span className="text-white font-bold text-lg">{title}</span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 text-gray-300 text-sm leading-relaxed">{children}</div>}
    </div>
  );
};

/* ─── Color Swatch (click to copy HEX) ─── */
const Swatch = ({ hex, name, usage }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(hex); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <button onClick={copy} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3 hover:bg-white/10 transition-all group text-left w-full">
      <div className="w-10 h-10 rounded-lg flex-shrink-0 border border-white/10" style={{ backgroundColor: hex }} />
      <div className="flex-1 min-w-0">
        <div className="text-white text-sm font-bold">{name}</div>
        <div className="text-gray-500 text-xs font-mono">{hex}</div>
        {usage && <div className="text-gray-400 text-[10px] mt-0.5">{usage}</div>}
      </div>
      {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
    </button>
  );
};

/* ─── Code Block (click to copy) ─── */
const Code = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative my-3">
      {label && <span className="text-xs text-gray-500 mb-1 block">{label}</span>}
      <pre className="bg-black/40 border border-white/10 rounded-lg p-4 overflow-x-auto text-xs text-green-400 font-mono whitespace-pre-wrap">{code}</pre>
      <button onClick={copy} className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 transition-colors">
        {copied ? <CheckCircle className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
      </button>
    </div>
  );
};

/* ─── Quick Nav ─── */
const NAV_ITEMS = [
  { id: "identita", label: "Identita'" },
  { id: "colori", label: "Colori" },
  { id: "logos", label: "Logo S" },
  { id: "tipografia", label: "Tipografia" },
  { id: "pacchetti", label: "Pacchetti" },
  { id: "cornici", label: "Cornici" },
  { id: "merch", label: "Merch" },
  { id: "tandem", label: "Tandem .com" },
  { id: "parolevietate", label: "Parole Vietate" },
  { id: "architettura", label: "Architettura" },
  { id: "contatti", label: "Contatti" },
  { id: "stampa", label: "Stampa" },
];

export default function BrandBook() {
  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="text-center mb-10">
          {/* Logo oro definitivo */}
          <div className="flex flex-col items-center gap-2 mb-4">
            <h2 className="text-3xl font-black text-[#E8B61B]">schedabusiness.it</h2>
            <p className="text-white/60 italic text-base">se non ti trovano, non esisti</p>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-2">Brand Book & Documentazione</h1>
          <p className="text-gray-500 text-sm">Guida completa per sviluppatori, grafici, ufficio stampa, tipografia e comunicazione</p>
        </div>

        {/* Quick Nav */}
        <div className="grid grid-cols-4 gap-2 mb-10">
          {NAV_ITEMS.map(n => (
            <a key={n.id} href={`#${n.id}`} className="text-center px-3 py-2.5 rounded-lg border border-white/10 text-gray-400 text-xs font-medium hover:border-white/30 hover:text-white transition-all bg-white/5">
              {n.label}
            </a>
          ))}
        </div>

        {/* 1. IDENTITA' */}
        <Section id="identita" title="Identita' del Brand" defaultOpen={true}>
          <div className="space-y-3">
            <p><strong className="text-white">Nome:</strong> schedabusiness.it</p>
            <p><strong className="text-white">Payoff:</strong> <em className="text-white/70">"se non ti trovano, non esisti"</em></p>
            <p><strong className="text-white">Fondatore:</strong> Riccardo Rossi — Fotografo Google Street View Certificato dal 2016 | Siena, Toscana</p>
            <p><strong className="text-white">Settore:</strong> Virtual Tour 360° per Agenzie Immobiliari, Enti Pubblici, Scuole, HORECA, Cantine, Wedding Venues, Studi Professionali</p>
            <p><strong className="text-white">Estetica:</strong> Dark by design. Sempre sfondo scuro (#0c1220 / #0a0e17). Mai sfondi chiari. Il nero fa risaltare i colori Google.</p>
            <p><strong className="text-white">Tono:</strong> Diretto, professionale con copy PNL "Closer". Sempre il TU (mai il "Lei"). Pattern interrupts. Target 50-65+ anni (Boomer-friendly, font grandi).</p>
            <p><strong className="text-white">Place ID Google:</strong> <a href="https://www.google.com/maps/place/?q=place_id:ChIJ8Zku0V47KhMRb5aqDGqIbU0" className="text-[#4285F4] hover:underline font-mono text-xs" target="_blank" rel="noopener noreferrer">ChIJ8Zku0V47KhMRb5aqDGqIbU0</a></p>
            <p><strong className="text-white">Link Condivisione:</strong> <a href="https://share.google/plYYOroJG18abeUnq" className="text-[#4285F4] hover:underline" target="_blank" rel="noopener noreferrer">share.google/plYYOroJG18abeUnq</a></p>
            <p><strong className="text-white">Relazione TANDEM:</strong> Il .it lavora in sinergia con schedabusiness.com. Cross-link reciproci, hreflang, identita' visiva allineata.</p>
          </div>
        </Section>

        {/* 2. PALETTE COLORI */}
        <Section id="colori" title="Palette Colori">
          <p className="mb-4">Palette basata sui colori Google. Click su qualsiasi colore per copiare il codice HEX.</p>

          <h4 className="text-white font-bold mb-3 text-base">Colori Primari (Google)</h4>
          <div className="grid grid-cols-2 gap-2 mb-6">
            <Swatch hex="#4285F4" name="Blu Google" usage="CTA, link, nav attivo, badge" />
            <Swatch hex="#EA4335" name="Rosso Google" usage="Urgenza, REC, header microfono, alert" />
            <Swatch hex="#FBBC04" name="Giallo Google" usage="Highlight, badge REGALO, avvisi" />
            <Swatch hex="#34A853" name="Verde Google" usage="Successo, conferma, invio, WhatsApp" />
          </div>

          <h4 className="text-white font-bold mb-3 text-base">Sfondi</h4>
          <div className="grid grid-cols-2 gap-2 mb-6">
            <Swatch hex="#0c1220" name="Background Primario" usage="Landing page, sezioni principali" />
            <Swatch hex="#0a0e17" name="Background Brand Book" usage="Pagine documentazione, admin" />
            <Swatch hex="#111827" name="Background Alternato" usage="Sezioni alternate (Tailwind gray-900)" />
            <Swatch hex="#0d1424" name="Background Card" usage="Card, sezioni collassabili" />
          </div>

          <h4 className="text-white font-bold mb-3 text-base">Accenti e CTA</h4>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Swatch hex="#ff6b35" name="Arancio CTA" usage="Bottoni principali call-to-action" />
            <Swatch hex="#9CA3AF" name="Grigio Testo" usage="Testo secondario (Tailwind gray-400)" />
          </div>

          <h4 className="text-white font-bold mb-3 text-base">Schema "schedabusiness" (Testo)</h4>
          <div className="flex items-center gap-0 mb-2 text-4xl font-black">
            <span style={{ color: "#4285F4" }}>sch</span>
            <span style={{ color: "#EA4335" }}>ed</span>
            <span style={{ color: "#FBBC04" }}>abu</span>
            <span style={{ color: "#4285F4" }}>sin</span>
            <span style={{ color: "#34A853" }}>ess</span>
            <span style={{ color: "#EA4335" }}>.it</span>
          </div>
          <p className="text-gray-400 text-xs">Ogni segmento del nome segue lo schema cromatico Google: Blu-Rosso-Giallo-Blu-Verde-Rosso</p>
        </Section>

        {/* 3. LOGO S ARCOBALENO */}
        <Section id="logos" title="Logo S Arcobaleno">
          <p className="mb-4">La "S" arcobaleno e' il simbolo visivo del brand. Composta da 6 bande con proporzioni esatte.</p>

          <div className="flex justify-center mb-6">
            <svg viewBox="0 0 120 160" className="w-32 h-40">
              <defs>
                <linearGradient id="sGradLarge" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4285F4" />
                  <stop offset="26%" stopColor="#4285F4" />
                  <stop offset="26%" stopColor="#EA4335" />
                  <stop offset="38%" stopColor="#EA4335" />
                  <stop offset="38%" stopColor="#FBBC04" />
                  <stop offset="50%" stopColor="#FBBC04" />
                  <stop offset="50%" stopColor="#4285F4" />
                  <stop offset="62%" stopColor="#4285F4" />
                  <stop offset="62%" stopColor="#34A853" />
                  <stop offset="74%" stopColor="#34A853" />
                  <stop offset="74%" stopColor="#EA4335" />
                  <stop offset="100%" stopColor="#EA4335" />
                </linearGradient>
              </defs>
              <text x="50%" y="120" textAnchor="middle" fill="url(#sGradLarge)" fontSize="140" fontWeight="900" fontFamily="Poppins, system-ui, sans-serif">S</text>
            </svg>
          </div>

          <h4 className="text-white font-bold mb-2 text-base">6 Bande — Proporzioni</h4>
          <div className="space-y-1 mb-4">
            {[
              { color: "#4285F4", name: "Blu Google", pct: "26%" },
              { color: "#EA4335", name: "Rosso Google", pct: "12%" },
              { color: "#FBBC04", name: "Giallo Google", pct: "12%" },
              { color: "#4285F4", name: "Blu Google", pct: "12%" },
              { color: "#34A853", name: "Verde Google", pct: "12%" },
              { color: "#EA4335", name: "Rosso Google", pct: "26%" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-3 rounded-sm" style={{ backgroundColor: b.color }} />
                <span className="text-gray-400 text-xs w-20">{b.name}</span>
                <span className="text-white text-xs font-mono">{b.pct}</span>
              </div>
            ))}
          </div>

          <Code label="CSS Gradient (copia-incolla)" code={`background: linear-gradient(
  to bottom,
  #4285F4 0%, #4285F4 26%,     /* Blu 26% */
  #EA4335 26%, #EA4335 38%,     /* Rosso 12% */
  #FBBC04 38%, #FBBC04 50%,     /* Giallo 12% */
  #4285F4 50%, #4285F4 62%,     /* Blu 12% */
  #34A853 62%, #34A853 74%,     /* Verde 12% */
  #EA4335 74%, #EA4335 100%     /* Rosso 26% */
);
-webkit-background-clip: text;
-webkit-text-fill-color: transparent;`} />

          <p className="text-gray-500 text-xs mt-2">Note: ".it" appare in bianco/40% a fianco della S, senza colore arcobaleno.</p>
        </Section>

        {/* 4. TIPOGRAFIA */}
        <Section id="tipografia" title="Tipografia">
          <h4 className="text-white font-bold mb-2 text-base">Font Primario</h4>
          <p className="mb-4">Poppins (Google Fonts) per titoli e corpo. system-ui come fallback.</p>
          <Code code={`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');`} />

          <h4 className="text-white font-bold mb-2 mt-6 text-base">Gerarchia (Boomer-friendly — Target 65+)</h4>
          <div className="space-y-2 mb-4">
            {[
              { level: "H1", size: "text-4xl sm:text-5xl lg:text-6xl", weight: "font-black (900)", example: "I tuoi ospiti decidono" },
              { level: "H2", size: "text-3xl sm:text-4xl", weight: "font-semibold (600)", example: "Fai parte di uno di questi settori?" },
              { level: "H3", size: "text-xl sm:text-2xl", weight: "font-bold (700)", example: "Pacchetto Formazione" },
              { level: "Body", size: "text-base (16px)", weight: "font-normal (400)", example: "Testo principale — mai sotto 16px" },
              { level: "Small", size: "text-sm (14px)", weight: "font-medium (500)", example: "Note secondarie — minimo storico" },
              { level: "Micro", size: "text-xs (12px)", weight: "font-bold (700)", example: "Badge, etichette, timestamp" },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-4 bg-white/5 rounded-lg px-4 py-2">
                <span className="text-[#4285F4] font-mono text-xs w-12">{t.level}</span>
                <span className="text-white text-xs flex-1">{t.size} / {t.weight}</span>
                <span className="text-gray-400 text-xs italic">"{t.example}"</span>
              </div>
            ))}
          </div>

          <h4 className="text-white font-bold mb-2 text-base">Payoff — Text Shadow</h4>
          <Code label="Glow bianco sotto il payoff" code={`text-shadow: 0 0 20px rgba(255,255,255,0.15);
font-style: italic;
color: rgba(255,255,255,0.5);`} />
        </Section>

        {/* 5. PACCHETTI */}
        <Section id="pacchetti" title="Pacchetti">
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="py-2 text-gray-400">Nome</th>
                  <th className="py-2 text-gray-400">Prezzo</th>
                  <th className="py-2 text-gray-400">Formazione</th>
                  <th className="py-2 text-gray-400">Colore</th>
                  <th className="py-2 text-gray-400">Route</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: "START", price: "€99", training: "+€199", color: "#4285F4", colorName: "Blu", route: "/#pacchetti" },
                  { name: "PLUS", price: "€299", training: "+€149", color: "#FBBC04", colorName: "Giallo", route: "/#pacchetti" },
                  { name: "VIRTUAL TOUR", price: "€499", training: "Inclusa", color: "#34A853", colorName: "Verde", route: "/#pacchetti" },
                  { name: "ROOFTOP", price: "€599", training: "IN REGALO", color: "#EA4335", colorName: "Rosso", route: "/#pacchetti" },
                ].map((p, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-3 font-bold" style={{ color: p.color }}>{p.name}</td>
                    <td className="py-3 text-white font-bold">{p.price}</td>
                    <td className="py-3">{p.training === "IN REGALO" ? <span className="text-[#34A853] font-bold">{p.training}</span> : p.training}</td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                        <span className="text-xs font-mono text-gray-400">{p.color}</span>
                      </div>
                    </td>
                    <td className="py-3 text-gray-400 text-xs font-mono">{p.route}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-400 text-xs">Tutti i pacchetti: PayPal 3 rate TAEG 0%. Deducibili 100% (art. 108 TUIR). Timewarp Hyperlapse IN REGALO per i 5 settori target.</p>
        </Section>

        {/* 6. CORNICI ARCOBALENO */}
        <Section id="cornici" title="Cornici Arcobaleno (Gradient Border)">
          <p className="mb-4">Le cornici arcobaleno si usano su max 4-5 elementi per pagina per evitare saturazione visiva. Applicare solo ai CTA principali.</p>

          <div className="flex justify-center mb-4">
            <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4285F4] via-[#FBBC04] to-[#EA4335]">
              <div className="bg-[#0c1220] rounded-xl px-8 py-4 text-white font-bold text-center">
                Esempio CTA con Cornice
              </div>
            </div>
          </div>

          <Code label="CSS — Cornice arcobaleno su bottone/card" code={`.rainbow-border {
  position: relative;
  padding: 2px;
  border-radius: 12px;
  background: linear-gradient(90deg, #4285F4, #EA4335, #FBBC04, #34A853, #4285F4);
}
.rainbow-border > * {
  background: #0c1220;
  border-radius: 10px;
}`} />

          <Code label="Tailwind — Cornice arcobaleno" code={`<div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4285F4] via-[#FBBC04] to-[#EA4335]">
  <div className="bg-[#0c1220] rounded-xl px-6 py-3">
    Contenuto
  </div>
</div>`} />
        </Section>

        {/* 7. MERCHANDISING */}
        <Section id="merch" title="Merchandising Total Black">
          <p className="mb-4">Tutto il merchandising e' su fondo nero. I colori Google risaltano sul nero puro. Mai sfondi colorati.</p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              "T-shirt nera con S arcobaleno",
              "Polo nera con logo ricamato",
              "Felpa nera con cappuccio + S",
              "Cappello nero con S frontale",
              "Tazza nera con S + payoff",
              "Adesivo S arcobaleno die-cut",
              "Block notes nero con logo",
              "Penna nera con incisione",
              "Zaino nero con patch S",
              "Cover telefono nera + S",
              "Badge/Spilla smaltata S",
              "Portachiavi S arcobaleno",
              "Biglietto da visita (vedi Stampa)",
              "Lanyard nero con S ripetuta",
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/5 rounded-lg px-3 py-2 text-xs text-gray-300 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#4285F4] to-[#EA4335] flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>

          <h4 className="text-white font-bold mb-2 text-base">Design T-Shirt</h4>
          <p className="text-gray-400 text-sm mb-2">Fronte: S arcobaleno grande (20cm) centrata sul petto. Retro: "schedabusiness.it" in bianco piccolo, collo.</p>
          <p className="text-gray-400 text-sm">Colore tessuto: nero puro (Pantone Black C). Stampa: serigrafia a 6 colori o DTF per il gradiente.</p>
        </Section>

        {/* 8. TANDEM .COM */}
        <Section id="tandem" title="Tandem schedabusiness.com">
          <p className="mb-4">Il .it e il .com lavorano in sinergia perfetta (Progetto TANDEM). Stessa identita' visiva, cross-link reciproci.</p>

          <h4 className="text-white font-bold mb-2 text-base">Cross-Link</h4>
          <ul className="space-y-1 mb-4 text-sm">
            <li>I pacchetti START / PLUS / ROOFTOP / VIRTUAL TOUR devono linkare anche a <strong className="text-[#4285F4]">schedabusiness.com</strong></li>
            <li>Il footer del .it contiene link al .com e viceversa</li>
            <li>Hreflang incrociati nel <code className="text-green-400 bg-black/30 px-1 rounded">index.html</code></li>
          </ul>

          <Code label="Hreflang (in index.html di entrambi i siti)" code={`<link rel="alternate" hreflang="it" href="https://schedabusiness.it" />
<link rel="alternate" hreflang="en" href="https://schedabusiness.com" />
<link rel="alternate" hreflang="x-default" href="https://schedabusiness.com" />`} />

          <h4 className="text-white font-bold mb-2 mt-4 text-base">Allineamento Visivo</h4>
          <ul className="space-y-1 text-sm">
            <li>Stesso sfondo dark (#0a0e17 / #0c1220)</li>
            <li>Stessa palette Google</li>
            <li>Stesso logo S arcobaleno (.it / .com)</li>
            <li>Stesso payoff "se non ti trovano, non esisti"</li>
            <li>Stesse cornici arcobaleno sui CTA principali</li>
          </ul>
        </Section>

        {/* 9. PAROLE VIETATE */}
        <Section id="parolevietate" title="Parole Vietate">
          <p className="mb-4 text-[#EA4335] font-bold">Queste parole NON devono MAI apparire nel sito, nelle email, nelle sponsorizzate o nel materiale stampato:</p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {["gratis", "sconto", "offerta", "SBP", "SOS", "economico", "low cost", "a buon mercato", "promozione", "promo", "coupon", "voucher"].map((w, i) => (
              <div key={i} className="bg-[#EA4335]/10 border border-[#EA4335]/30 rounded-lg px-3 py-2 text-center text-[#EA4335] text-sm font-bold line-through">
                {w}
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-sm"><strong className="text-white">Perche:</strong> Il brand comunica valore, non prezzo. Non siamo economici — siamo accessibili. Non facciamo sconti — facciamo regali (Timewarp IN REGALO). Il tono e' da "Closer", non da volantino del supermercato.</p>
        </Section>

        {/* 10. ARCHITETTURA TECNICA */}
        <Section id="architettura" title="Architettura Tecnica">
          <h4 className="text-white font-bold mb-2 text-base">Stack</h4>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-white/5 rounded-lg p-3">
              <div className="text-white font-bold text-sm mb-1">Frontend</div>
              <div className="text-gray-400 text-xs">React 18 + Craco, Tailwind 3, Framer Motion, Lucide, Shadcn/UI, Pannellum</div>
            </div>
            <div className="bg-white/5 rounded-lg p-3">
              <div className="text-white font-bold text-sm mb-1">Backend</div>
              <div className="text-gray-400 text-xs">Python 3.11, FastAPI, MongoDB (Motor), Uvicorn ASGI</div>
            </div>
          </div>

          <h4 className="text-white font-bold mb-2 text-base">Route Principali</h4>
          <div className="space-y-1 mb-4">
            {[
              { path: "/", desc: "Landing B2B principale" },
              { path: "/hotel", desc: "Landing HORECA" },
              { path: "/cantine", desc: "Landing Cantine" },
              { path: "/wedding", desc: "Landing Wedding" },
              { path: "/dentisti", desc: "Landing Studi Professionali" },
              { path: "/agenzie", desc: "Database agenzie Toscana" },
              { path: "/email", desc: "Template email" },
              { path: "/enti", desc: "Strategia enti pubblici" },
              { path: "/roadmap", desc: "Piano commerciale" },
              { path: "/admin", desc: "Documentazione tecnica" },
              { path: "/brand-book", desc: "Questa pagina" },
              { path: "/tour/:id", desc: "Viewer 360 Pannellum" },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-xs">
                <code className="text-[#4285F4] font-mono w-28">{r.path}</code>
                <span className="text-gray-400">{r.desc}</span>
              </div>
            ))}
          </div>

          <h4 className="text-white font-bold mb-2 text-base">API Endpoints</h4>
          <div className="space-y-1">
            {[
              { method: "GET", path: "/api/tours", desc: "Lista tour" },
              { method: "GET", path: "/api/tours/:id", desc: "Dettaglio tour" },
              { method: "POST", path: "/api/contact", desc: "Form contatto" },
              { method: "POST", path: "/api/voice-note", desc: "Nota vocale (audio + cases)" },
              { method: "GET", path: "/api/stats", desc: "Statistiche" },
            ].map((a, i) => (
              <div key={i} className="flex items-center gap-3 text-xs">
                <span className={`font-mono w-12 font-bold ${a.method === "GET" ? "text-[#34A853]" : "text-[#FBBC04]"}`}>{a.method}</span>
                <code className="text-gray-300 font-mono w-40">{a.path}</code>
                <span className="text-gray-500">{a.desc}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* 11. CONTATTI */}
        <Section id="contatti" title="Contatti e Riferimenti">
          <div className="space-y-3">
            <p><strong className="text-white">Telefono:</strong> +39 327 383 9782</p>
            <p><strong className="text-white">WhatsApp:</strong> <a href="https://wa.me/+393273839782" className="text-[#34A853] hover:underline">wa.me/+393273839782</a></p>
            <p><strong className="text-white">Sito .it:</strong> <a href="https://schedabusiness.it" className="text-[#4285F4] hover:underline">schedabusiness.it</a></p>
            <p><strong className="text-white">Sito .com:</strong> <a href="https://schedabusiness.com" className="text-[#4285F4] hover:underline">schedabusiness.com</a></p>
            <p><strong className="text-white">Google Maps:</strong> <a href="https://share.google/plYYOroJG18abeUnq" className="text-[#4285F4] hover:underline">Scheda Business.com — Apri su Google Maps</a></p>
            <p><strong className="text-white">Place ID:</strong> <code className="text-green-400 bg-black/30 px-2 py-0.5 rounded text-xs">ChIJ8Zku0V47KhMRb5aqDGqIbU0</code></p>
            <p><strong className="text-white">Sede:</strong> Colle di Val d'Elsa, Siena, Toscana</p>
          </div>
        </Section>

        {/* 12. STAMPA */}
        <Section id="stampa" title="Per Tipografia e Stampa">
          <h4 className="text-white font-bold mb-2 text-base">Conversione CMYK</h4>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="py-2 text-gray-400">Colore</th>
                  <th className="py-2 text-gray-400">HEX</th>
                  <th className="py-2 text-gray-400">CMYK</th>
                  <th className="py-2 text-gray-400">Pantone (ref)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: "Blu Google", hex: "#4285F4", cmyk: "73/45/0/4", pantone: "2727 C" },
                  { name: "Rosso Google", hex: "#EA4335", cmyk: "0/71/77/8", pantone: "1795 C" },
                  { name: "Giallo Google", hex: "#FBBC04", cmyk: "0/25/97/2", pantone: "7549 C" },
                  { name: "Verde Google", hex: "#34A853", cmyk: "69/0/50/34", pantone: "7481 C" },
                  { name: "Nero Brand", hex: "#0a0e17", cmyk: "0/0/0/95", pantone: "Black C" },
                ].map((c, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: c.hex }} />
                      <span className="text-white">{c.name}</span>
                    </td>
                    <td className="py-2 font-mono text-gray-400">{c.hex}</td>
                    <td className="py-2 font-mono text-gray-400">{c.cmyk}</td>
                    <td className="py-2 text-gray-400">{c.pantone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="text-white font-bold mb-2 text-base">Serigrafia Logo S</h4>
          <p className="text-gray-400 text-sm mb-4">6 passaggi colore (uno per banda). Per tirature alte, valutare DTF (Direct to Film) che riproduce il gradiente in un passaggio unico.</p>

          <h4 className="text-white font-bold mb-2 text-base">Biglietto da Visita</h4>
          <p className="text-gray-400 text-sm mb-2">Formato: 85x55mm. Carta: Materica Nero 360g. Stampa: serigrafia bianca + 4 colori Google. Finitura: opaca.</p>
          <div className="bg-black border border-white/20 rounded-xl p-5 max-w-sm mx-auto mb-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-xl font-black">
                <span style={{ color: "#4285F4" }}>sch</span>
                <span style={{ color: "#EA4335" }}>ed</span>
                <span style={{ color: "#FBBC04" }}>abu</span>
                <span style={{ color: "#4285F4" }}>sin</span>
                <span style={{ color: "#34A853" }}>ess</span>
                <span style={{ color: "#EA4335" }}>.it</span>
              </div>
            </div>
            <div className="text-white text-xs">Riccardo Rossi</div>
            <div className="text-gray-400 text-[10px]">Fotografo Google Street View Certificato</div>
            <div className="text-gray-500 text-[10px] mt-2">+39 327 383 9782 | schedabusiness.it</div>
            <div className="flex gap-1 mt-2">
              {["#4285F4","#EA4335","#FBBC04","#4285F4","#34A853","#EA4335"].map((c,i)=>(
                <div key={i} className="w-1.5 h-1.5 rounded-full" style={{backgroundColor:c}} />
              ))}
            </div>
          </div>

          <h4 className="text-white font-bold mb-2 mt-4 text-base">QR Code</h4>
          <p className="text-gray-400 text-sm">Generare QR code che punta a <code className="text-green-400 bg-black/30 px-1 rounded">https://schedabusiness.it</code>. Colore QR: bianco su nero. Mai invertire. Dimensione minima stampa: 2x2cm.</p>
        </Section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-lg font-black">
                <span style={{ color: "#4285F4" }}>sch</span>
                <span style={{ color: "#EA4335" }}>ed</span>
                <span style={{ color: "#FBBC04" }}>abu</span>
                <span style={{ color: "#4285F4" }}>sin</span>
                <span style={{ color: "#34A853" }}>ess</span>
                <span style={{ color: "#EA4335" }}>.it</span>
              </div>
              <p className="text-white/30 text-xs italic mt-1">se non ti trovano, non esisti</p>
            </div>
            <div className="text-right">
              <p className="text-gray-600 text-xs">© 2026 schedabusiness.it — Brand Book v1.0</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
